import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Message } from '../../chat/schemas';
import { ChatBody } from '../../chat/components/ChatBody';
import { ChatFooter } from '../../chat/components/ChatFooter';
import styles from './FloatingChat.module.css';

interface FloatingChatProps {
  isOpen: boolean;
  onToggle: () => void;
  context?: string; // Optional context to pass to the chat
}

interface WindowSize {
  width: number;
  height: number;
  x: number;
  y: number;
}

export const FloatingChat: React.FC<FloatingChatProps> = ({ isOpen, onToggle, context }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [retrievalUrls, setRetrievalUrls] = useState<string[]>([]);
  const chatTitle = 'Policyora';
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isResizing = false;
  const [windowSize, setWindowSize] = useState<WindowSize>({ 
    width: 300, 
    height: 420, 
    x: typeof window !== 'undefined' ? window.innerWidth - 320 : 0, 
    y: typeof window !== 'undefined' ? window.innerHeight - 440 : 0 
  });
  
  const { token, user } = useAuth();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const threadIdRef = useRef<string>(`floating-${Date.now()}`);
  const api_url = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8000";
  
  // Store the position before going fullscreen
  const prevWindowSize = useRef<WindowSize | null>(null);

  // We no longer need to create sessions manually - the backend will handle it

  const handleSendMessage = async (userMessage: string) => {
    if (!user?.email || !userMessage.trim()) return;

    // Add user message to chat
    const userMsg: Message = {
      id: Date.now(),
      sender: 'user',
      text: userMessage,
    };

    setMessages(prev => [...prev, userMsg]);
    setIsThinking(true);
    setRetrievalUrls([]);

    // Use existing session ID or null (let backend create a new one)
    const currentSessionId = sessionId;
    
    try {
      console.log('Sending message to:', `${api_url}/api/v1/chat/user_chat`);
      const response = await fetch(`${api_url}/api/v1/chat/user_chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_query: userMessage,
          user_email: user.email,
          thread_id: threadIdRef.current,
          session_id: currentSessionId // Use the session ID we created
        }),
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Update session ID from response if we didn't have one
      if (data.session_id && !sessionId) {
        console.log('Setting new session ID from response:', data.session_id);
        setSessionId(data.session_id);
      }
      
      // Handle the response format from the working chat page
      const responseText = data.query_response || data.response || data.message || 'No response from the server';
      const sources = data.retrieval_urls || [];
      
      const assistantMsg: Message = {
        id: Date.now() + 1,
        sender: 'bot',
        text: responseText,
        sources: Array.isArray(sources) ? sources : [sources],
      };

      setMessages(prev => [...prev, assistantMsg]);
      setRetrievalUrls(Array.isArray(sources) ? sources : [sources]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg: Message = {
        id: Date.now() + 1,
        sender: 'bot',
        text: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        sources: [],
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsThinking(false);
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isThinking]);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      // Store current size and position before going fullscreen
      prevWindowSize.current = { ...windowSize };
      setWindowSize({
        width: window.innerWidth - 40,
        height: window.innerHeight - 80,
        x: 20,
        y: 20
      });
    } else if (prevWindowSize.current) {
      // Restore previous size and position
      setWindowSize(prevWindowSize.current);
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen, windowSize]);

  const startResize = useCallback((e: React.MouseEvent, direction: 'top' | 'right' | 'bottom' | 'left' | 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left') => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = windowSize.width;
    const startHeight = windowSize.height;
    const startLeft = windowSize.x;
    const startTop = windowSize.y;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      
      setWindowSize(prev => {
        const newSize = { ...prev };
        
        // Handle right edge
        if (direction.includes('right')) {
          newSize.width = Math.min(
            Math.max(300, startWidth + dx),
            window.innerWidth - startLeft - 20
          );
        }
        
        // Handle left edge
        if (direction.includes('left')) {
          const newWidth = Math.min(
            Math.max(300, startWidth - dx),
            startLeft + startWidth - 20
          );
          newSize.x = startLeft + (startWidth - newWidth);
          newSize.width = newWidth;
        }
        
        // Handle bottom edge
        if (direction.includes('bottom')) {
          newSize.height = Math.min(
            Math.max(300, startHeight + dy),
            window.innerHeight - startTop - 20
          );
        }
        
        // Handle top edge
        if (direction.includes('top')) {
          const newHeight = Math.min(
            Math.max(300, startHeight - dy),
            startTop + startHeight - 20
          );
          newSize.y = startTop + (startHeight - newHeight);
          newSize.height = newHeight;
        }
        
        return newSize;
      });
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [windowSize.height, windowSize.width, windowSize.x, windowSize.y]);

  const startDrag = useCallback((e: React.MouseEvent) => {
    if (isFullscreen) return;
    
    e.preventDefault();
    const startX = e.clientX - windowSize.x;
    const startY = e.clientY - windowSize.y;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newX = Math.max(
        20, // Min 20px from left
        Math.min(
          moveEvent.clientX - startX,
          window.innerWidth - windowSize.width - 20 // Keep 20px from right
        )
      );
      
      const newY = Math.max(
        20, // Min 20px from top
        Math.min(
          moveEvent.clientY - startY,
          window.innerHeight - windowSize.height - 20 // Keep 20px from bottom
        )
      );
      
      setWindowSize(prev => ({
        ...prev,
        x: newX,
        y: newY
      }));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isFullscreen, windowSize.height, windowSize.width, windowSize.x, windowSize.y]);

  if (!isOpen) {
    return (
      <button className={styles.floatingButton} onClick={onToggle}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </button>
    );
  }

  return (
    <div 
      ref={floatingRef}
      className={`${styles.floatingChatContainer} ${isFullscreen ? styles.fullscreen : ''} ${isResizing ? styles.resizing : ''}`}
      style={{
        width: `${windowSize.width}px`,
        height: `${windowSize.height}px`,
        left: isFullscreen ? '20px' : `${windowSize.x}px`,
        top: isFullscreen ? '20px' : `${windowSize.y}px`,
        transition: isResizing ? 'none' : 'all 0.2s ease',
      }}
    >
      {/* Resize handles */}
      {!isFullscreen && (
        <>
          <div className={`${styles.resizeHandle} ${styles.top}`} onMouseDown={(e) => startResize(e, 'top')}></div>
          <div className={`${styles.resizeHandle} ${styles.right}`} onMouseDown={(e) => startResize(e, 'right')}></div>
          <div className={`${styles.resizeHandle} ${styles.bottom}`} onMouseDown={(e) => startResize(e, 'bottom')}></div>
          <div className={`${styles.resizeHandle} ${styles.left}`} onMouseDown={(e) => startResize(e, 'left')}></div>
          <div className={`${styles.resizeHandle} ${styles.topRight}`} onMouseDown={(e) => startResize(e, 'top-right')}></div>
          <div className={`${styles.resizeHandle} ${styles.bottomRight}`} onMouseDown={(e) => startResize(e, 'bottom-right')}></div>
          <div className={`${styles.resizeHandle} ${styles.bottomLeft}`} onMouseDown={(e) => startResize(e, 'bottom-left')}></div>
          <div className={`${styles.resizeHandle} ${styles.topLeft}`} onMouseDown={(e) => startResize(e, 'top-left')}></div>
        </>
      )}
      
      <div 
        className={styles.chatHeader} 
        onMouseDown={startDrag}
      >
        <h3>{chatTitle}</h3>
        <div className={styles.headerActions}>
          <button 
            onClick={toggleFullscreen} 
            className={styles.fullscreenButton} 
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
          <button onClick={onToggle} className={styles.closeButton} aria-label="Close chat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className={styles.chatBody} ref={chatContainerRef}>
        <ChatBody 
          messages={messages} 
          isThinking={isThinking} 
          retrievalUrls={retrievalUrls} 
          showPlaceholder={false}
        />
      </div>
      
      <div className={styles.chatFooter}>
        <ChatFooter 
          fetchResponse={handleSendMessage} 
          isGenerating={isThinking}
        />
      </div>
    </div>
  );
};
